import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  getMyOrders,
  getOrderById,
  createOrderFromCart,
  getAllOrdersForAdmin,
  updateOrderStatus as updateOrderStatusRequest,
} from "../api/orders.service";

export const useOrdersStore = defineStore("orders", () => {
  const orders = ref([]);
  const currentOrder = ref(null);
  const adminOrders = ref([]);
  const isLoading = ref(false);
  const error = ref(null);

  function normalizeOrders(value) {
    return Array.isArray(value) ? value : [];
  }

  const totalOrdersCount = computed(() => orders.value.length);

  const sortedOrders = computed(() => {
    return [...orders.value].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    );
  });

  const totalSpent = computed(() => {
    return orders.value.reduce((sum, order) => sum + order.totalAmount, 0);
  });

  const sortedAdminOrders = computed(() => {
    return [...adminOrders.value].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    );
  });

  const totalAdminAmount = computed(() => {
    return adminOrders.value.reduce((sum, order) => sum + order.totalAmount, 0);
  });

  function clearError() {
    error.value = null;
  }

  function applyUpdatedOrder(updatedOrder) {
    if (!updatedOrder?.id) return;

    const updateInList = (listRef) => {
      const index = listRef.value.findIndex(
        (order) => order.id === updatedOrder.id,
      );
      if (index !== -1) {
        listRef.value[index] = {
          ...listRef.value[index],
          ...updatedOrder,
          clientName:
            updatedOrder.clientName || listRef.value[index].clientName,
          clientEmail:
            updatedOrder.clientEmail || listRef.value[index].clientEmail,
          clientPhone:
            updatedOrder.clientPhone || listRef.value[index].clientPhone,
          assignedToUserName:
            updatedOrder.assignedToUserName ||
            listRef.value[index].assignedToUserName, 
        };
      }
    };

    updateInList(orders);
    updateInList(adminOrders);

    if (currentOrder.value?.id === updatedOrder.id) {
      currentOrder.value = {
        ...currentOrder.value,
        ...updatedOrder,
        clientName: updatedOrder.clientName || currentOrder.value.clientName,
        clientEmail: updatedOrder.clientEmail || currentOrder.value.clientEmail,
        clientPhone: updatedOrder.clientPhone || currentOrder.value.clientPhone,
        assignedToUserName:
          updatedOrder.assignedToUserName ||
          currentOrder.value.assignedToUserName,
      };
    }
  }

  async function fetchMyOrders() {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await getMyOrders();
      const data = normalizeOrders(response?.data);
      orders.value = data;
      return data;
    } catch (err) {
      error.value = err.response?.data?.message || "Ошибка загрузки заказов";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchOrderById(orderId) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await getOrderById(orderId);
      const data = response?.data ?? null;
      currentOrder.value = data;
      return data;
    } catch (err) {
      error.value = err.response?.data?.message || "Ошибка загрузки заказа";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function createOrder(cartId) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await createOrderFromCart(cartId);
      await fetchMyOrders();
      return response?.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Ошибка создания заказа";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchAllOrdersForAdmin() {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await getAllOrdersForAdmin();
      const data = normalizeOrders(response?.data);
      adminOrders.value = data;
      return data;
    } catch (err) {
      error.value = err.response?.data?.message || "Ошибка загрузки заказов";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateOrderStatus(orderId, status) {
    error.value = null;

    try {
      const response = await updateOrderStatusRequest(orderId, status);
      const data = response?.data ?? null;
      applyUpdatedOrder(data);
      return data;
    } catch (err) {
      error.value =
        err.response?.data?.message || "Ошибка смены статуса заказа";
      throw err;
    }
  }

  function clearCurrentOrder() {
    currentOrder.value = null;
  }

  function clearAllOrders() {
    orders.value = [];
    adminOrders.value = [];
    currentOrder.value = null;
    error.value = null;
  }

  return {
    orders,
    currentOrder,
    adminOrders,
    isLoading,
    error,

    totalOrdersCount,
    sortedOrders,
    totalSpent,
    sortedAdminOrders,
    totalAdminAmount,

    clearError,
    fetchMyOrders,
    fetchOrderById,
    createOrder,
    fetchAllOrdersForAdmin,
    updateOrderStatus,
    clearCurrentOrder,
    clearAllOrders,
  };
});
